import type { SavedObjectiveRules } from 'definitions';
import type { SavedObjectiveSelect } from 'types';
import { useState } from 'react';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import {
  isEmptyObjectives,
  objectivesFromExperiment,
  objectivesHash,
  objectivesLocked,
} from 'helpers/objectives-identity';
import { apiErrorMessage } from '../api-error';
import { useExperiment } from './useExperiment';

export type SaveMode = 'new' | 'update';

type DialogState = { kind: 'save'; mode: SaveMode } | { kind: 'load'; preselectedId: number | null } | null;

/** What a load replaced, as POST /objectives/apply returns it; Undo posts it back to /objectives/restore. */
type PreviousObjectives = { rules: SavedObjectiveRules; sourceId: number | null; sourceHash: string | null };

type RenameParams = { id: number; name: string };

const MENU_LIMIT = 5;
const UNDO_MS = 8000;

/**
 * Everything the Saved objectives menu and its two dialogs need for one experiment: the website's
 * entries, the experiment's current objectives, where they were loaded from and whether they
 * changed since, which dialog is open, and the actions. Mirrors useSavedTargetings.
 *
 * The actions toast their own result and return whether they succeeded, so a dialog can stay open
 * on a failure (a taken name) with the API's message shown.
 */
export const useSavedObjectives = (experimentId: number) => {
  const { experiment } = useExperiment(experimentId);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const apiClient = useClient();
  const [dialog, setDialog] = useState<DialogState>(null);

  const websiteId = experiment?.websiteId;
  const listKey = ['SAVED_OBJECTIVES', websiteId];

  const { data: savedObjectives = [], isLoading } = useQuery<SavedObjectiveSelect[]>({
    enabled: !!websiteId,
    queryKey: listKey,
    queryFn: () => apiClient.get<SavedObjectiveSelect[]>(`/saved-objectives/website/${websiteId}`),
  });

  const currentRules = objectivesFromExperiment(experiment ?? {});
  const isEmpty = isEmptyObjectives(currentRules);
  // A deleted source finds nothing and the line hides. Unlike targeting, an empty card still came
  // from its source: the website default with no secondaries is a set of objectives too.
  const source = savedObjectives.find((entry) => entry.id === experiment?.objectivesSourceId);
  const editedSinceLoad = !!source && objectivesHash(currentRules) !== experiment?.objectivesSourceHash;
  // Objectives are fixed while running, so nothing can be loaded onto it (the API refuses too).
  const isLocked = objectivesLocked(experiment?.status);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: listKey }),
      queryClient.invalidateQueries({ queryKey: ['EXPERIMENT', experimentId] }),
    ]);
  };

  const run = async (action: () => Promise<void>, fallback: string): Promise<boolean> => {
    try {
      await action();
      return true;
    } catch (error) {
      toast.error(apiErrorMessage(error, fallback));
      return false;
    }
  };

  const closeDialog = () => setDialog(null);
  const openSave = (mode: SaveMode) => setDialog({ kind: 'save', mode });
  const openLoad = (preselectedId: number | null = null) => setDialog({ kind: 'load', preselectedId });

  /**
   * Close before refreshing: once the list refetches it holds the name just saved, and an open
   * Save dialog would flash its own "name already exists" check at the user for a render. A failed
   * request throws before this point, so the dialog stays open with the API's message.
   */
  const saveNew = (name: string) =>
    run(async () => {
      await apiClient.post('/saved-objectives', { experimentId, name: name.trim() });
      closeDialog();
      toast.success(`Saved "${name.trim()}"`);
      await refresh();
    }, 'Could not save the objectives.');

  const updateExisting = (entry: SavedObjectiveSelect) =>
    run(async () => {
      await apiClient.put(`/saved-objectives/${entry.id}/rules`, { experimentId });
      closeDialog();
      toast.success(`Updated "${entry.name}"`);
      await refresh();
    }, 'Could not update the saved objectives.');

  const restore = (previous: PreviousObjectives) =>
    run(async () => {
      await apiClient.post(`/experiments/${experimentId}/objectives/restore`, previous);
      await refresh();
      toast.success('Previous objectives restored.');
    }, 'Could not undo the load.');

  const load = (entry: SavedObjectiveSelect) =>
    run(async () => {
      const { previous } = await apiClient.post<{ previous: PreviousObjectives }>(
        `/experiments/${experimentId}/objectives/apply`,
        { savedObjectiveId: entry.id },
      );
      await refresh();
      closeDialog();
      // One level of undo, held by this toast: a later load shows its own.
      toast.success(`Loaded "${entry.name}". Previous objectives replaced.`, {
        duration: UNDO_MS,
        action: { label: 'Undo', onClick: () => void restore(previous) },
      });
    }, 'Could not load the saved objectives.');

  /** From the menu: an empty card loses nothing, so it loads at once; otherwise confirm first. */
  const loadFromMenu = (entry: SavedObjectiveSelect) => {
    if (isLocked) return;
    if (isEmpty) return load(entry);
    openLoad(entry.id);
  };

  const rename = ({ id, name }: RenameParams) =>
    run(async () => {
      await apiClient.patch(`/saved-objectives/${id}`, { name: name.trim() });
      await refresh();
    }, 'Could not rename the saved objectives.');

  const remove = (id: number) =>
    run(async () => {
      await apiClient.delete(`/saved-objectives/${id}`);
      await refresh();
    }, 'Could not delete the saved objectives.');

  return {
    savedObjectives,
    recent: savedObjectives.slice(0, MENU_LIMIT),
    isLoading,
    currentRules,
    isEmpty,
    source,
    editedSinceLoad,
    isLocked,
    // The API is read-only for Members; hiding the menu spares them a 403.
    canEdit: !!user && user.role !== 'Member',
    dialog,
    openSave,
    openLoad,
    closeDialog,
    saveNew,
    updateExisting,
    load,
    loadFromMenu,
    rename,
    remove,
  };
};

export type SavedObjectivesManager = ReturnType<typeof useSavedObjectives>;
